import {
  parsePhoneNumberFromString,
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
} from 'libphonenumber-js';
// Bundled with libphonenumber-js — not a new dependency, and 4 KB: one example national number
// per country. It is what intl-tel-input uses to build its per-country placeholders.
import phoneExamples from 'libphonenumber-js/examples.mobile.json';

/**
 * ONE place that decides what a stored phone number may look like.
 *
 * Written after a customer was saved with phone = "+112096025553300" and BOTH address
 * columns null (live row fae1acdb, 2026-08-30): the street number had bled into the phone
 * field and the address write was lost. Whatever produced it, nothing between the input and
 * the database ever asked "is this a phone number?" — createOrLinkCustomer passed the raw
 * string to the RPC, and that function is the shared creation path for AddCustomerModal,
 * GoLiveStep and QuoteBuilder.
 *
 * WHY THIS NOW USES libphonenumber-js (2026-09-13, operator ruling)
 * The rules below were hand-rolled and NANP-shaped: 10 digits meant +1, 11 digits starting
 * with 1 meant +1, and anything else was kept as typed. The Customers phone control was
 * built on the same assumption and offered 13 hardcoded country codes, so a number from
 * anywhere else could not be entered — and worse, could not be READ BACK: the picker fell
 * back to +1 and showed a US flag over an Egyptian number. Live at the time, 3 of 248 stored
 * customer phones carried a calling code outside that list (+60 Malaysia, +20, +29…).
 *
 * libphonenumber-js is the library behind intl-tel-input and Google's own phone handling, so
 * this is the standard rather than a bigger version of the guess. It also replaces the
 * length heuristics with real per-country metadata.
 *
 * STILL DELIBERATELY CONSERVATIVE — this is a validating boundary, not a strict validator:
 *   - letters mean it is not a phone number (an address, a note) -> null
 *   - an explicitly INTERNATIONAL value (starts with +) that the parser cannot place -> null
 *     Keeping the old strictness on purpose: "+112096025553300" is the value that prompted
 *     this file. A caller that typed + is claiming to know the country code, so we hold them
 *     to it rather than storing something plainly wrong.
 *   - otherwise, if the parser says the number is POSSIBLE, store canonical E.164
 *   - anything else -> digits kept AS TYPED, not discarded
 * The last rule matters: an unusual but genuine number must not be silently destroyed just
 * because it does not fit a shape we recognise. Only clearly-not-a-phone input is dropped.
 *
 * POSSIBLE, NOT VALID, is the test — and the difference is load-bearing. isValid() checks
 * the number against real allocated ranges, which rejects 555-01xx directory-test numbers
 * and anything newly allocated since the bundled metadata was cut. The placeholders and the
 * examples in the error messages now come from examples.mobile.json — official example numbers,
 * which are exactly the kind isValid() is entitled to reject. A boundary that refuses the number
 * printed in its own placeholder is a boundary people route around. isPossible() checks length
 * for the country, which is what we actually want to know.
 */

/** Fallback country when the user typed no + and we have nothing better. */
export const DEFAULT_COUNTRY = 'US';

/**
 * Canonical storage form: E.164 ("+15417050524"), or null when the value is not a phone
 * number at all.
 *
 * @param {string} value
 * @param {string} [defaultCountry] ISO-3166 alpha-2, used only when `value` has no leading +
 */
export const normalizePhone = (value, defaultCountry = DEFAULT_COUNTRY) => {
  if (value === null || value === undefined) return null;

  const raw = String(value).trim();
  if (raw === '') return null;

  // Letters are never part of a phone number. This is the case that actually bit:
  // "11209 N Tatum Blvd" reaching the phone column.
  if (/[a-zA-Z]/.test(raw)) return null;

  const parsed = parsePhoneNumberFromString(raw, defaultCountry);

  if (raw.startsWith('+')) {
    // They named a country code. Hold them to it — see the header note on +112096025553300.
    return parsed && parsed.isPossible() ? parsed.number : null;
  }

  if (parsed && parsed.isPossible()) return parsed.number;

  const digits = raw.replace(/\D/g, '');
  if (digits === '') return null;

  // Not a shape the metadata recognises. Keep what was typed rather than throw away a real
  // number — but it has already been proven to contain no letters.
  return digits;
};

/** True when a value is storable as a phone number at all. */
export const isPlausiblePhone = (value, defaultCountry = DEFAULT_COUNTRY) =>
  normalizePhone(value, defaultCountry) !== null;

/** 🇬🇧 from "GB" — regional indicator letters, no image assets or icon font needed. */
const flagEmoji = (iso) =>
  String(iso)
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));

// Country NAMES come from the platform, not from a table we would have to maintain (and get
// wrong, and let drift). Intl.DisplayNames is in every browser this app supports; the ISO
// code is the fallback if it is ever missing.
const regionNames =
  typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function'
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

/**
 * Every country libphonenumber-js knows (~240), sorted by name.
 *
 * The picker stores an ISO COUNTRY, not a calling code, because a calling code is not a
 * country: +1 alone is the US, Canada and nineteen Caribbean nations. Storing the country is
 * what lets the control show the right flag when the value is read back.
 */
export const COUNTRY_OPTIONS = getCountries()
  .map((iso) => ({
    iso,
    callingCode: `+${getCountryCallingCode(iso)}`,
    name: regionNames?.of(iso) || iso,
    flag: flagEmoji(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

const COUNTRY_BY_ISO = new Map(COUNTRY_OPTIONS.map((c) => [c.iso, c]));

/** The calling code for an ISO country, e.g. "GB" -> "+44". */
export const callingCodeFor = (iso) => COUNTRY_BY_ISO.get(iso)?.callingCode || '';

/**
 * A country to DISPLAY for a calling code that does not pin one region.
 *
 * The picker only ever shows a flag and a calling code, so what has to be right is the code.
 * Prefer the caller's default when it already shares the code (a US default for a +1 number
 * stays US, and a user who picked United Kingdom is not moved to Guernsey); otherwise take the
 * conventional primary region, falling back to whichever country the metadata lists.
 */
const PRIMARY_REGION_FOR_CALLING_CODE = {
  '+1': 'US', '+7': 'RU', '+44': 'GB', '+47': 'NO', '+61': 'AU',
  '+212': 'MA', '+262': 'RE', '+358': 'FI', '+590': 'GP', '+599': 'CW',
};

const countryForCallingCode = (code, defaultCountry = DEFAULT_COUNTRY) => {
  const isos = COUNTRIES_BY_CALLING_CODE[code] || [];
  if (isos.length === 0) return defaultCountry;
  if (isos.length === 1) return isos[0];
  if (isos.includes(defaultCountry)) return defaultCountry;
  const primary = PRIMARY_REGION_FOR_CALLING_CODE[code];
  return isos.includes(primary) ? primary : isos[0];
};

/**
 * Split a stored value for EDITING: which country it belongs to, and the national part to
 * show in the text box.
 *
 * This replaces a hand-written longest-prefix match over 13 known codes, which returned the
 * whole string unsplit for anything else — so the text box showed "+60255510349" while the
 * picker claimed +1.
 */
export const splitPhone = (value, defaultCountry = DEFAULT_COUNTRY) => {
  const raw = String(value ?? '').trim();
  if (raw === '') return { country: defaultCountry, nationalNumber: '' };

  const parsed = parsePhoneNumberFromString(raw, defaultCountry);
  if (parsed?.country) {
    return { country: parsed.country, nationalNumber: parsed.nationalNumber || '' };
  }

  // Parsed to a calling code we cannot pin to ONE country (+1 shared by 21 nations, or
  // metadata that knows the code but not the region). Keep the digits after the code so the
  // box never shows a raw E.164 string.
  //
  // Board #1617: this used to return defaultCountry unconditionally, which threw away a
  // calling code the value plainly carried. A number stored as +442065550123 — valid for no
  // +44 REGION, so parsed.country is undefined — came back as US, so the picker showed +1 and
  // the box showed "(206) 555-0123" while the preview beside it read "+44 20 6555 0123".
  // Worse than the mismatch: the next edit recomposed against the picker and rewrote a UK
  // number as a US one. Resolve a country that shares the parsed calling code instead.
  if (parsed?.countryCallingCode) {
    return {
      country: countryForCallingCode(`+${parsed.countryCallingCode}`, defaultCountry),
      nationalNumber: parsed.nationalNumber || '',
    };
  }

  // Unparseable — which is the NORMAL state mid-typing, and the state a value containing
  // letters is always in. Strip the calling code we know we put on the front so the caller
  // gets the national part back; returning `raw` here would put "+1abcdefg" in the text box
  // and make the offending characters impossible to find.
  //
  // `country` is NULL here, not `defaultCountry`. Board #4944 (filed eight ways, 2026-10-07
  // to 10-09): this branch used to answer `defaultCountry`, i.e. US, for a value it had NOT
  // parsed, and PhoneField's picker-follow effect cannot tell "parsed as US" from "did not
  // parse, here is the default". Pick United Kingdom, type one digit, value is "+447" (too
  // short to parse), this said US, the picker snapped to +1 and the box showed "+447".
  // Two earlier fixes (#1967 paste-a-plus, #1617 shared calling codes) each patched a
  // symptom elsewhere and left this line; the e2e spec never saw it because fill() sets the
  // whole number in one event. A caller that needs SOME country uses `?? defaultCountry`.
  const ownCode = callingCodeFor(defaultCountry);
  if (ownCode && raw.startsWith(ownCode)) {
    return { country: null, nationalNumber: raw.slice(ownCode.length) };
  }
  return { country: null, nationalNumber: raw };
};

/**
 * How a stored number should READ on a list or a detail panel.
 *
 * National format for the viewer's own country ("(541) 705-0524") and international for
 * everywhere else ("+44 7911 123456"). The previous version formatted only 10-digit and
 * 11-digit-leading-1 values, so every non-US number rendered as raw E.164 — and, worse, a
 * UK number wrongly stored as +1 rendered in US parens and looked entirely plausible.
 */
export const formatPhoneDisplay = (value, viewerCountry = DEFAULT_COUNTRY) => {
  const raw = String(value ?? '').trim();
  if (raw === '') return '';

  const parsed = parsePhoneNumberFromString(raw, viewerCountry);
  if (!parsed) return raw;

  return parsed.country === viewerCountry
    ? parsed.formatNational()
    : parsed.formatInternational();
};

/** Progressive formatting while typing, e.g. "541" -> "(541" -> "(541) 705-0524". */
export const formatAsYouType = (nationalInput, country = DEFAULT_COUNTRY) =>
  new AsYouType(country).input(String(nationalInput ?? ''));

/**
 * A real example number for a country, in that country's own national format — "(201) 555-0123"
 * for US, "07400 123456" for GB, "012-345 6789" for MY.
 *
 * Board #1361. Everything that told the user what a phone number should look like was hardcoded
 * to the US: the placeholder read "555 123 4567" and the validation error said "digits only, 10
 * for a US number" — both of them regardless of which of the ~240 countries was selected in the
 * picker beside the box. Telling someone who has chosen +44 that a number is 10 digits is not a
 * cosmetic slip; it is instructions to enter the wrong thing.
 *
 * The examples come from metadata rather than a list we would write, for the same reason the
 * country names come from Intl.DisplayNames: a hand-kept table of 240 formats drifts, and the
 * 13-country hardcoded list this file replaced is what that drift looks like.
 */
export const phoneExampleFor = (iso) => {
  try {
    const example = getExampleNumber(iso, phoneExamples);
    return example ? example.formatNational() : '';
  } catch (_) {
    return '';
  }
};

/**
 * Which country a (possibly invalid) value belongs to, or null.
 *
 * Needed because the value we have to explain is by definition one the parser REJECTED, and
 * parsePhoneNumberFromString returns no `country` for those. But the control composes the value
 * as callingCode + national digits, so the code on the front is still the user's stated country
 * even when the rest is wrong — so fall back to matching that prefix.
 *
 * Only when the calling code belongs to exactly ONE country: +1 covers the US, Canada and
 * nineteen Caribbean nations, and naming one of them would be a guess dressed as a fact.
 */
const COUNTRIES_BY_CALLING_CODE = COUNTRY_OPTIONS.reduce((acc, c) => {
  (acc[c.callingCode] = acc[c.callingCode] || []).push(c.iso);
  return acc;
}, {});

export const countryOfPhone = (value, defaultCountry = DEFAULT_COUNTRY) => {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  if (!raw.startsWith('+')) return defaultCountry;

  const parsed = parsePhoneNumberFromString(raw, defaultCountry);
  if (parsed?.country) return parsed.country;

  // Longest match wins: +1 and +1242 are both real prefixes, and +1242 is Bahamas specifically.
  const codes = Object.keys(COUNTRIES_BY_CALLING_CODE)
    .filter((code) => raw.startsWith(code))
    .sort((a, b) => b.length - a.length);
  for (const code of codes) {
    const isos = COUNTRIES_BY_CALLING_CODE[code];
    if (isos.length === 1) return isos[0];
  }
  return null;
};

/** The longest calling code on the front of a value, whether or not it pins one country. */
const callingCodeOf = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw.startsWith('+')) return '';
  return Object.keys(COUNTRIES_BY_CALLING_CODE)
    .filter((code) => raw.startsWith(code))
    .sort((a, b) => b.length - a.length)[0] || '';
};

/**
 * How many national digits a calling code expects, when every country sharing that code agrees.
 *
 * This exists because the two codes that matter most are SHARED and so cannot name a country:
 * +1 is the US, Canada and nineteen Caribbean nations, +44 is the UK plus Guernsey, Jersey and
 * the Isle of Man. Naming one of them would be a guess, and quoting one of their example numbers
 * could hand a Guernsey user a London number. The digit count is the part they all agree on and
 * is the one fact the user needs, so that is what gets said.
 *
 * Returns null when the group disagrees, in which case the message makes no length claim at all.
 */
const nationalDigitsForCode = (callingCode) => {
  const isos = COUNTRIES_BY_CALLING_CODE[callingCode] || [];
  const lengths = new Set();
  for (const iso of isos) {
    try {
      const example = getExampleNumber(iso, phoneExamples);
      if (example?.nationalNumber) lengths.add(String(example.nationalNumber).length);
    } catch (_) { /* a country with no example contributes nothing */ }
  }
  return lengths.size === 1 ? [...lengths][0] : null;
};

/**
 * Why a value was refused, in terms of the country the user actually chose (board #1361).
 *
 * Lives here rather than in customerValidation because this file is the owner of "what a stored
 * phone number may look like" — the message has to agree with isPlausiblePhone, and two files
 * each holding half of that rule is how the 13-country list came to disagree with the picker.
 */
export const phoneErrorMessage = (value, defaultCountry = DEFAULT_COUNTRY) => {
  const raw = String(value ?? '').trim();

  // Named first because it is the one cause the user can act on without knowing anything about
  // country formats, and it is the case this validation was originally written for: an address
  // bleeding into the phone box.
  if (/[a-zA-Z]/.test(raw)) {
    return 'A phone number cannot contain letters — remove them and try again.';
  }

  const iso = countryOfPhone(raw, defaultCountry);
  if (iso) {
    const name = COUNTRY_BY_ISO.get(iso)?.name || iso;
    // "a phone number for Germany" rather than "a Germany phone number": country NAMES are
    // nouns, not adjectives, and an a/an rule over ~240 of them would be one more thing to get
    // wrong (Ireland, Iceland, the United Arab Emirates).
    const example = phoneExampleFor(iso);
    return example
      ? `That does not look like a phone number for ${name}. One looks like ${example}.`
      : `That does not look like a phone number for ${name} — check the digits.`;
  }

  // A code we know but that several countries share — +1 and +44, i.e. the two most common cases
  // here. Say the code and the digit count they agree on rather than picking one of them.
  const code = callingCodeOf(raw);
  if (code) {
    const digits = nationalDigitsForCode(code);
    return digits
      ? `That does not look like a ${code} phone number — they have ${digits} digits after the code.`
      : `That does not look like a ${code} phone number — check the digits after the code.`;
  }

  return raw.startsWith('+')
    ? 'That calling code is not one we recognise — check the country code at the front of the number.'
    : 'That does not look like a phone number — check the digits.';
};
